import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { formatDate, formatDateTime, todayLocal } from "../../api/dates";
import { formatMoney, formatPercent, formatQuantity, formatRate, toCents } from "../../api/money";
import {
  useCancelOrder,
  useMarkSent,
  useOrder,
  useOrderMove,
  usePriceLine,
  useRecordSupplierInvoice,
  useUpdateOrderHeader,
  type OrderDetail,
  type OrderLine,
} from "../../api/purchaseOrders";
import { StatusChip } from "../../components/ui";
import { jobLink } from "../commercial/pipeline";
import { orderLabel, orderTone } from "../commercial/tones";

interface Props {
  id: number;
}

/** One order: header, lines priced while still ours, the document, the moves
 * this account may make, and what the supplier eventually charged. */
export default function OrderWorkspace({ id }: Props) {
  const order = useOrder(id);
  if (order.isPending) {
    return <p className="empty-state">Loading the order…</p>;
  }
  if (order.isError) {
    return <p className="notice bad">{order.error.message}</p>;
  }
  return <OrderView detail={order.data} />;
}

/** How many lines still have no price -- a line copied from the quotation at
 * nothing, which the supplier would receive as a free part. */
function unpricedCount(detail: OrderDetail): number {
  return detail.lines.filter((line) => toCents(line.unit_cost) === 0).length;
}

export function OrderView({ detail }: { detail: OrderDetail }) {
  const unpriced = unpricedCount(detail);
  return (
    <section className="cm-docview" aria-label={`Purchase order ${detail.number}`}>
      <div className="cm-doc-head">
        <h2 className="mono">{detail.number}</h2>
        <StatusChip tone={orderTone(detail.status)}>{orderLabel(detail.status)}</StatusChip>
        {unpriced > 0 && detail.actions.can_price ? <StatusChip tone="warn">{unpriced} unpriced</StatusChip> : null}
        <span className="cm-doc-end cm-doc-links">
          <a className="btn" href={detail.pdf_url}>
            Purchase order (PDF)
          </a>
          {detail.quotation ? (
            <a className="btn" href={detail.pack_url}>
              Order + quotation (PDF)
            </a>
          ) : null}
        </span>
      </div>

      <dl className="cm-facts">
        <div>
          <dt>Supplier</dt>
          <dd>{detail.supplier ? `${detail.supplier.name} (${detail.supplier.acc_no})` : "—"}</dd>
        </div>
        <div>
          <dt>From quotation</dt>
          <dd>
            {detail.quotation ? (
              <Link to={jobLink(detail.quotation.id, "quote")}>{detail.quotation.number}</Link>
            ) : (
              "—"
            )}
          </dd>
        </div>
        <div>
          <dt>Issued</dt>
          <dd>{detail.issued_at ? formatDateTime(detail.issued_at) : "Not yet"}</dd>
        </div>
        <div>
          <dt>Sent</dt>
          <dd>
            {detail.sent_at
              ? `${formatDate(detail.sent_at)} to ${detail.sent_to ?? "—"}${detail.sent_by ? ` by ${detail.sent_by}` : ""}`
              : "Not yet"}
          </dd>
        </div>
        <div>
          <dt>Acknowledged</dt>
          <dd>{detail.acknowledged_at ? formatDateTime(detail.acknowledged_at) : "Not yet"}</dd>
        </div>
        <div>
          <dt>Order date</dt>
          <dd>{formatDate(detail.po_date)}</dd>
        </div>
        {detail.expiry_ack ? (
          <div>
            <dt>Expired prices used because</dt>
            <dd>{detail.expiry_ack}</dd>
          </div>
        ) : null}
      </dl>

      <Header detail={detail} />
      <Lines detail={detail} />

      <div className="cm-doc-bottom">
        <div className="cm-doc-bottom-text">
          <Standing detail={detail} unpriced={unpriced} />
          <SupplierInvoice detail={detail} />
        </div>
        <table className="cm-foot">
          <tbody>
            <tr>
              <td>Total excl VAT</td>
              <td>{formatMoney(detail.totals.total_excl_vat)}</td>
            </tr>
            <tr>
              <td>VAT{detail.totals.vatable ? ` at ${formatRate(detail.vat_rate)}` : " (supplier not VAT-registered)"}</td>
              <td>{formatMoney(detail.totals.vat)}</td>
            </tr>
            <tr className="total">
              <td>Total incl VAT</td>
              <td>{formatMoney(detail.totals.grand_total)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Where the order stands, in words, and the moves this account may make.
 * Every status has its own sentence: an acknowledged order is still waiting
 * for the supplier's invoice, not finished (Fix #8). */
export function standingNote(detail: OrderDetail, unpriced: number): string {
  const supplier = detail.supplier?.name ?? "the supplier";
  switch (detail.status) {
    case "pending_generation":
    case "draft":
    case "generated":
      return unpriced > 0
        ? `${unpriced === 1 ? "One line has" : `${unpriced} lines have`} no price yet. Price ${unpriced === 1 ? "it" : "them"} before issuing: ${supplier} receives what the PDF says.`
        : `Ready to issue. ${supplier} receives the PDF.`;
    case "issued":
      return detail.actions.office
        ? `Issued ${formatDateTime(detail.issued_at)}. Email it to ${supplier}, then mark it sent.`
        : "Issued to you. Acknowledge it once you have it.";
    case "sent":
      // The day only: a send dated in the past is stored at midday, so a time
      // would claim a precision nobody recorded.
      return detail.actions.office
        ? `Sent to ${detail.sent_to ?? supplier} on ${formatDate(detail.sent_at)}. Waiting for ${supplier}'s acknowledgement.`
        : "Issued to you. Acknowledge it once you have it.";
    case "acknowledged":
      if (detail.supplier_invoice !== null) {
        return `Acknowledged ${formatDateTime(detail.acknowledged_at)}, and ${supplier}'s invoice is recorded.`;
      }
      return detail.actions.office
        ? `Acknowledged ${formatDateTime(detail.acknowledged_at)}. Record ${supplier}'s invoice when it arrives.`
        : `Acknowledged ${formatDateTime(detail.acknowledged_at)}.`;
    case "cancelled":
      if (detail.quotation && ["returned", "resubmitted"].includes(detail.quotation.status)) {
        return `Cancelled, and quotation ${detail.quotation.number} reopened for revision. Its replacement order is raised when the revised quote is approved; this number is kept.`;
      }
      return "Cancelled. Nothing more can happen to this order; its number is kept.";
  }
}

function Standing({ detail, unpriced }: { detail: OrderDetail; unpriced: number }) {
  const move = useOrderMove(detail.id);
  const [cancelling, setCancelling] = useState(false);
  const [sending, setSending] = useState(false);
  const a = detail.actions;
  const supplier = detail.supplier?.name ?? "the supplier";
  const any = a.can_issue || a.can_mark_sent || a.can_acknowledge || a.can_cancel;
  return (
    <section className="cm-block">
      <h3>Order status</h3>
      <p className="cm-doc-note">{standingNote(detail, unpriced)}</p>
      {any ? (
        <div className="move-buttons">
          {a.can_issue ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={move.isPending}
              onClick={() => move.mutate("issue")}
            >
              Issue to {supplier}
            </button>
          ) : null}
          {a.can_mark_sent && !sending ? (
            <button type="button" className="btn btn-primary" onClick={() => setSending(true)}>
              Mark as sent
            </button>
          ) : null}
          {a.can_acknowledge ? (
            <button type="button" className="btn btn-primary" disabled={move.isPending} onClick={() => move.mutate("acknowledge")}>
              Acknowledge receipt
            </button>
          ) : null}
          {a.can_cancel && !cancelling ? (
            <button type="button" className="btn btn-danger" disabled={move.isPending} onClick={() => setCancelling(true)}>
              Cancel order
            </button>
          ) : null}
        </div>
      ) : null}
      {sending ? <MarkSent detail={detail} onDone={() => setSending(false)} /> : null}
      {cancelling ? <CancelOrder detail={detail} onKeep={() => setCancelling(false)} /> : null}
      {move.isError ? <p className="notice bad">{move.error.message}</p> : null}
    </section>
  );
}

/** Where, and on which day, the issued order was emailed. "Sent to" is filled
 * from the address this supplier's last order went to and "Sent on" with
 * today; the office corrects either if the order went elsewhere or earlier.
 * Both are needed, so the button waits for both. The server has the last word
 * on a day in the future or before the order was issued. */
function MarkSent({ detail, onDone }: { detail: OrderDetail; onDone: () => void }) {
  const send = useMarkSent(detail.id);
  const [to, setTo] = useState(detail.suggested_sent_to ?? "");
  const [sentOn, setSentOn] = useState(todayLocal);
  function onSubmit(event: FormEvent) {
    event.preventDefault();
    send.mutate({ to: to.trim(), sent_on: sentOn }, { onSuccess: onDone });
  }
  return (
    <form className="cm-inline-box" onSubmit={onSubmit} aria-label={`Mark ${detail.number} as sent`}>
      <label className="cm-grow">
        Sent to
        <input type="email" required value={to} onChange={(event) => setTo(event.target.value)} autoFocus />
      </label>
      <label>
        Sent on
        <input type="date" required value={sentOn} max={todayLocal()} onChange={(event) => setSentOn(event.target.value)} />
      </label>
      <button type="submit" className="btn btn-primary" disabled={send.isPending || to.trim() === "" || sentOn === ""}>
        Record as sent
      </button>
      <button type="button" className="btn" onClick={onDone}>
        Not yet
      </button>
      {send.isError ? <p className="notice bad">{send.error.message}</p> : null}
    </form>
  );
}

/** Cancelling asks why, and whether the quote is to be revised: cancelled and
 * reopened, the quotation's lines can be edited, added to or removed on the
 * Supplier quote step, and resubmitted and approved into a replacement order.
 * Or the order is simply cancelled and the quote stays as it was approved. */
function CancelOrder({ detail, onKeep }: { detail: OrderDetail; onKeep: () => void }) {
  const cancel = useCancelOrder(detail.id);
  const navigate = useNavigate();
  const [reason, setReason] = useState("");
  const blocker = detail.actions.revise_blocker;
  const quotation = detail.quotation;
  const why = reason.trim();

  return (
    <div className="cm-cancel" role="group" aria-label={`Cancel ${detail.number}?`}>
      <label className="field">
        Why is the order being cancelled?
        <textarea
          rows={2}
          maxLength={1000}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Macsteel quoted the wrong seal kit; the quote needs the 150 mm kit"
        />
      </label>
      {quotation && blocker ? <p className="notice warn">The quote cannot be revised from here: {blocker}</p> : null}
      <div className="move-buttons">
        {quotation && !blocker ? (
          <button
            type="button"
            className="btn btn-primary"
            disabled={cancel.isPending || why === ""}
            title={why === "" ? "Say why first: the reopened quote carries it" : undefined}
            onClick={() =>
              cancel.mutate(
                { reason: why, revise: true },
                { onSuccess: () => navigate(jobLink(quotation.id, "quote")) },
              )
            }
          >
            Cancel and revise quote {quotation.number}
          </button>
        ) : null}
        <button type="button" className="btn btn-danger" disabled={cancel.isPending} onClick={() => cancel.mutate({ reason: why || null, revise: false })}>
          Cancel the order only
        </button>
        <button type="button" className="btn" disabled={cancel.isPending} onClick={onKeep}>
          Keep the order
        </button>
      </div>
      {quotation && !blocker ? (
        <p className="cm-doc-note">
          Revising reopens the quote on the Supplier quote step, where its lines can be edited, added or removed. Resubmit and approve it and a
          replacement order is raised; {detail.number} stays cancelled under its own number.
        </p>
      ) : null}
      {cancel.isError ? <p className="notice bad">{cancel.error.message}</p> : null}
    </div>
  );
}

function Header({ detail }: { detail: OrderDetail }) {
  const update = useUpdateOrderHeader(detail.id);
  const [poDate, setPoDate] = useState(detail.po_date ?? "");
  const [reference, setReference] = useState(detail.reference ?? "");
  const [eta, setEta] = useState(detail.eta ?? "");
  if (!detail.actions.office || detail.status === "cancelled") {
    return null;
  }
  function onSubmit(event: FormEvent) {
    event.preventDefault();
    update.mutate({ po_date: poDate || null, reference: reference.trim() || null, eta: eta || null });
  }
  return (
    <form className="cm-inline-box" onSubmit={onSubmit} aria-label="Order header">
      <label>
        Order date
        <input type="date" value={poDate} onChange={(e) => setPoDate(e.target.value)} />
      </label>
      <label>
        Supplier's reference
        <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Their order ack no." />
      </label>
      <label>
        Expected delivery
        <input type="date" value={eta} onChange={(e) => setEta(e.target.value)} />
      </label>
      <button type="submit" className="btn btn-secondary" disabled={update.isPending}>
        Save header
      </button>
      {update.isError ? <p className="notice bad">{update.error.message}</p> : null}
    </form>
  );
}

/** The lines as the order prints them. The first column is the line's place
 * on the order; the item code and the supplier's own code have their own
 * columns (Fix #1: `#` used to show the item code and `Code` the supplier's,
 * and a five-digit code wrapped in a column sized for two). */
function Lines({ detail }: { detail: OrderDetail }) {
  const price = usePriceLine(detail.id);
  const [editing, setEditing] = useState<number | null>(null);
  const [cost, setCost] = useState("");
  const [discount, setDiscount] = useState("");
  const canPrice = detail.actions.can_price;

  function start(line: OrderLine) {
    setEditing(line.id);
    setCost(toCents(line.unit_cost) === 0 ? "" : line.unit_cost);
    setDiscount(line.discount_pct);
  }

  return (
    <div className="table-card">
      <table className="data-table cm-lines" aria-label="Order lines">
        <thead>
          <tr>
            <th className="cm-seq">#</th>
            <th>Item code</th>
            <th>Their code</th>
            <th>Details</th>
            <th className="num">Qty</th>
            <th className="num">Unit cost</th>
            <th className="num">Disc</th>
            <th className="num">Amount</th>
          </tr>
        </thead>
        <tbody>
          {detail.lines.map((line, index) => {
            const unpriced = toCents(line.unit_cost) === 0;
            const meta = [line.size, line.pressure_nominal, line.delivery].filter(Boolean).join(" · ");
            return (
              <tr key={line.id} className={unpriced ? "cm-line-warn" : undefined}>
                <td className="cm-seq muted">{index + 1}</td>
                <td className="mono">{line.item_code}</td>
                <td className="mono muted">{line.supplier_code ?? ""}</td>
                <td>
                  {line.details}
                  {meta ? <span className="sub">{meta}</span> : null}
                </td>
                <td className="num">{formatQuantity(line.units)}</td>
                <td className="num">
                  {editing === line.id ? (
                    <span className="cm-price-edit">
                      <input value={cost} onChange={(e) => setCost(e.target.value)} aria-label="Unit cost" className="input num" autoFocus />
                      <input value={discount} onChange={(e) => setDiscount(e.target.value)} aria-label="Discount" className="input num cm-disc" />
                      <button
                        type="button"
                        className="row-button cm-save"
                        disabled={price.isPending || cost.trim() === ""}
                        onClick={() =>
                          price.mutate(
                            { lineId: line.id, unit_cost: cost, discount_pct: discount },
                            { onSuccess: () => setEditing(null) },
                          )
                        }
                      >
                        Save
                      </button>
                      <button type="button" className="row-button" onClick={() => setEditing(null)}>
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <span className="cm-price">
                      <span className={unpriced ? "cm-needs-price" : undefined}>
                        {unpriced ? "Needs a price" : formatMoney(line.unit_cost)}
                      </span>
                      {canPrice ? (
                        <button type="button" className="row-button" onClick={() => start(line)} aria-label={`Price line ${index + 1}`}>
                          Price
                        </button>
                      ) : null}
                    </span>
                  )}
                </td>
                <td className="num muted">{formatPercent(line.discount_pct)}</td>
                <td className="num">{unpriced ? "—" : formatMoney(line.amount)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {price.isError ? <p className="notice bad">{price.error.message}</p> : null}
    </div>
  );
}

/** Whether what the supplier charged differs from the order, compared as
 * cents: "1000" and "1000.00" are the same amount (Fix #3 -- the strings were
 * compared, and said they differed). */
export function invoiceDiffers(amount: string | null, orderTotal: string): boolean {
  if (amount === null) {
    return false;
  }
  const charged = toCents(amount);
  const ordered = toCents(orderTotal);
  return charged !== null && ordered !== null && charged !== ordered;
}

function SupplierInvoice({ detail }: { detail: OrderDetail }) {
  const record = useRecordSupplierInvoice(detail.id);
  const [number, setNumber] = useState(detail.supplier_invoice?.number ?? "");
  const [amount, setAmount] = useState(detail.supplier_invoice?.amount ?? "");
  const [date, setDate] = useState(detail.supplier_invoice?.date ?? "");
  const invoice = detail.supplier_invoice;
  const differs = invoice !== null && invoiceDiffers(invoice.amount, detail.totals.total_excl_vat);

  return (
    <section className="cm-block">
      <h3>Supplier's invoice</h3>
      {invoice !== null ? (
        <p className={differs ? "cm-doc-note warn" : "cm-doc-note ok"}>
          Invoice <b>{invoice.number}</b> for <b>{formatMoney(invoice.amount)}</b>
          {invoice.date ? `, dated ${formatDate(invoice.date)}` : ""}
          {differs ? ` — differs from the order's ${formatMoney(detail.totals.total_excl_vat)}.` : ", matching the order."}
        </p>
      ) : (
        <p className="cm-doc-note">Not recorded yet. The tracker's gross profit subtracts this figure.</p>
      )}
      {detail.actions.can_record_invoice ? (
        <form
          className="inline-form"
          aria-label="Record the supplier's invoice"
          onSubmit={(event) => {
            event.preventDefault();
            record.mutate({ number: number.trim(), amount, invoice_date: date || null, replace: invoice !== null });
          }}
        >
          <label>
            Their invoice no.
            <input value={number} onChange={(e) => setNumber(e.target.value)} required />
          </label>
          <label>
            Amount excl VAT
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="num"
              placeholder={detail.totals.total_excl_vat}
              required
            />
          </label>
          <label>
            Dated
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <button type="submit" className="btn btn-secondary" disabled={record.isPending}>
            {invoice !== null ? "Replace" : "Record"}
          </button>
        </form>
      ) : null}
      {record.isError ? <p className="notice bad">{record.error.message}</p> : null}
    </section>
  );
}
